import type { GetStaticPropsContext } from 'next'

import { resolveMeta, type TMetaTemplate } from '~/utils/seo'

/**
 * Centralized helper function to resolve SEO metadata for pages
 * Uses locale-aware SEO templates provided by App.getInitialProps
 */
export const resolvePageSeoMeta = async (
  context: GetStaticPropsContext,
  metaProps: any,
  templateType: TMetaTemplate = 'page'
) => {
  try {
    // Get SEO templates from centralized _app.tsx loading
    const seoTemplates = (context as any)?.pageProps?.seoTemplates

    if (seoTemplates?.[templateType] && metaProps) {
      const metaTemplates = Object.assign(
        {},
        seoTemplates[templateType],
        metaProps.meta
      )

      metaProps.meta = await resolveMeta(metaProps._id, metaTemplates)
    } else {
      metaProps.meta = {
        title: metaProps.meta?.title || 'MyArtBroker',
        description: metaProps.meta?.description || 'Default description',
      }
    }
  } catch (error) {
    console.log('Error resolving metadata:', error)
    // Fallback to basic meta on error
    metaProps.meta = {
      title: metaProps.meta?.title || 'MyArtBroker',
      description: metaProps.meta?.description || 'Default description',
    }
  }

  return metaProps
}

/**
 * Helper function for utility functions that receive SEO templates directly
 * Used by utility functions that don't have access to GetStaticPropsContext
 */
export const resolveMetaWithTemplates = async (
  metaProps: any,
  seoTemplates: any,
  templateType: TMetaTemplate = 'page',
  categoryParam?: string
) => {
  try {
    if (seoTemplates?.[templateType] && metaProps) {
      const metaTemplates = {
        ...seoTemplates[templateType],
        ...metaProps.meta,
      }

      metaProps.meta = await resolveMeta(metaProps._id, metaTemplates)
    } else {
      // Fallback when SEO templates aren't available
      console.log(
        `SEO templates not available for type: ${templateType}, using basic meta`
      )
      metaProps.meta = {
        title: metaProps.meta?.title || 'MyArtBroker',
        description: metaProps.meta?.description || 'Default description',
      }
    }
  } catch (error) {
    console.log('Error resolving metadata:', error)
    // Fallback to basic meta on error
    metaProps.meta = {
      title: metaProps.meta?.title || 'MyArtBroker',
      description: metaProps.meta?.description || 'Default description',
    }
  }

  // Handle category-specific title override
  if (categoryParam) {
    const title = categoryParam
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')

    if (!metaProps.meta.title || metaProps.meta.title === 'MyArtBroker') {
      metaProps.meta.title = `${title} Articles | MyArtBroker Magazine`
    }
  }

  return metaProps
}
